import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { LoadingState } from '../../../shared/types/loading.type';
import {
  ButtonComponent,
  InputComponent,
  ModalData,
  ModalRef,
  SkeletonDirective,
} from '@ui-lib/components';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AgentsService } from '../agents.service';
import { IAgentFormControls, IAgentModalData } from './agents-modal.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, Observable } from 'rxjs';
import { FacilitySelectComponent } from '../../../shared/components/facility-select/facility-select.component';
import { ModalAction } from '../agents.model';
import { PermissionService } from '../../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../../core/models/user-permissions.enum';

@Component({
  standalone: true,
  selector: 'mon-agent-modal',
  templateUrl: './agents-modal.component.html',
  styleUrls: ['./agents-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    TranslateModule,
    FacilitySelectComponent,
    SkeletonDirective,
  ],
  providers: [AgentsService],
})
export class AgentsModalComponent extends ModalData<IAgentModalData> implements OnInit {
  protected readonly agentsService = inject(AgentsService);
  protected readonly LoadingState = LoadingState;
  private readonly modalRef = inject(ModalRef<boolean>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  protected readonly canManageAgents = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.AgentManage),
    { initialValue: false },
  );
  protected readonly canReadFacilities = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FacilityRead),
    { initialValue: false },
  );

  public readonly agentForm = new FormGroup<IAgentFormControls>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    facilityId: new FormControl(null, { validators: [Validators.required] }),
    comment: new FormControl('', { nonNullable: true }),
  });

  public ngOnInit(): void {
    this.agentForm.patchValue({
      name: this.modalData.agentName,
    });
    if (this.modalData.action === ModalAction.APPROVE) return;

    this.agentsService
      .getById(this.modalData.agentId)
      .pipe(
        filter((agent) => !!agent),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((agent) => {
        this.agentForm.patchValue({
          facilityId: agent.facilityId
            ? {
                id: agent.facilityId,
                label: agent.facilityName ?? agent.facilityId,
              }
            : null,
          comment: agent.comment ?? '',
        });
      });
  }

  public onSubmit(): void {
    if (!this.canManageAgents() || !this.canReadFacilities() || this.agentForm.invalid) return;

    const action$ =
      this.modalData.action === ModalAction.APPROVE ? this.submitApprove() : this.submitEdit();

    action$
      .pipe(
        filter((result) => !!result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.closeModal(result);
      });
  }

  private submitEdit(): Observable<boolean> {
    const value = this.agentForm.getRawValue();
    return this.agentsService.updateAgent({
      id: this.modalData.agentId,
      name: value.name,
      facilityId: value.facilityId?.id ?? '',
      comment: value.comment,
    });
  }

  private submitApprove(): Observable<boolean> {
    const value = this.agentForm.getRawValue();
    return this.agentsService.approveAgent({
      id: this.modalData.agentId,
      isApproved: true,
      name: value.name,
      facilityId: value.facilityId?.id ?? '',
      comment: value.comment,
    });
  }

  public closeModal(result: boolean): void {
    this.modalRef.close(result);
  }
}
